import * as React from 'react';
import Avatar from 'antd/lib/avatar';
import Segmented, { SegmentedLabeledOption } from 'antd/lib/segmented';
import ConfigProvider from 'antd/lib/config-provider';
import { useSidebarColors } from '../../styles/themeMode';
import { AppPlatform, AppStoreType } from '../../../types';
import { Destination } from '../../../types/destinations';
import WorkflowsPlatformIcon from '../../icons/WorkflowsPlatformIcon';
import { useMdmPlatformOverride } from '../../../helpers/mdmPlatformOverride';
import { useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { selectAppSettings } from '../../../selectors/selectors';
import { selectAvailableDestinations } from '../../../selectors/summaryViewSelectors';
import { AppRouteComponentMatchParams } from '../../../typescriptHelpers';
import routes from '../../../routes';

interface OrgRouteParams {
    orgIdParam: string;
}

export const PlatformIconBox: React.FC<{
    platform: AppPlatform;
    appStoreType?: AppStoreType;
    size?: number;
}> = ({ platform, appStoreType, size = 28 }) => {
    return <WorkflowsPlatformIcon platform={platform} appStoreType={appStoreType} width={size} height={size} />;
};

export const DestinationIcon: React.FC<{
    destination: Destination;
    size?: number;
    platformIconOverride?: (size: number) => React.ReactNode;
}> = ({ destination, size = 16, platformIconOverride }) => {
    const sc = useSidebarColors();
    const mdmOverride = useMdmPlatformOverride(destination.appId);
    const iconOverride = platformIconOverride ?? mdmOverride?.icon;
    const orgId = useRouteMatch<OrgRouteParams>(routes.org)?.params?.orgIdParam;
    const routeAppId = useRouteMatch<AppRouteComponentMatchParams>(routes.app)?.params?.appIdParam;
    const appId = destination.appId ?? routeAppId;
    const orgDestinations = useSelector((state: RootState) => selectAvailableDestinations(state, orgId));
    const appDestinationsById = useSelector(
        (state: RootState) => selectAppSettings(state, appId)?.destinationInfoByDestinationStepConfigId,
    );

    const availableDestinations = (() => {
        if (appDestinationsById) {
            return Object.values(appDestinationsById);
        }

        if (appId) {
            return orgDestinations.filter((orgDestination) => orgDestination.appId === appId);
        }

        return [];
    })();

    if (destination.iconURL) {
        return (
            <Avatar
                shape="square"
                size={size}
                src={destination.iconURL}
                style={{
                    borderRadius: size * 0.17,
                    flexShrink: 0,
                }}
            />
        );
    }

    const letter = destination.name?.charAt(0).toUpperCase();

    if (letter && hasSameTypeSibling(destination, availableDestinations)) {
        return (
            <Avatar
                shape="square"
                size={size}
                style={{
                    borderRadius: size * 0.17,
                    backgroundColor: sc.icon,
                    fontSize: Math.round(size * 0.7),
                    flexShrink: 0,
                }}
            >
                {letter}
            </Avatar>
        );
    }

    return (
        <>
            {iconOverride?.(size) ?? (
                <WorkflowsPlatformIcon
                    platform={destination.platform}
                    appStoreType={destination.appStoreType}
                    width={size}
                    height={size}
                />
            )}
        </>
    );
};

// Checks if there are multiple destinations with the same platform/app store type that could lead to a duplicate icon
export const hasSameTypeSibling = (destination: Destination, siblings: Destination[]): boolean =>
    siblings.filter(
        (sibling) => sibling.platform === destination.platform && sibling.appStoreType === destination.appStoreType,
    ).length > 1;

export const NavigationSegmented: React.FC<{
    value: string;
    onChange: (value: string | number) => void;
    options: SegmentedLabeledOption[];
    style?: React.CSSProperties;
}> = ({ value, onChange, options, style = {} }) => {
    const sc = useSidebarColors();
    const navigationSegmentedTheme = {
        components: {
            Segmented: {
                trackBg: sc.segmentedBg,
                trackPadding: 4,
                itemColor: sc.icon,
                itemHoverColor: sc.textSecondary,
                itemHoverBg: 'transparent',
                itemSelectedBg: sc.segmentedActiveBg,
                itemSelectedColor: sc.text,
                itemBorder: sc.border,
            },
        },
    };
    return (
        <ConfigProvider theme={navigationSegmentedTheme}>
            <Segmented
                block
                value={value}
                onChange={onChange}
                className="app-menu-segmented"
                style={{ ...style, borderRadius: 8 }}
                options={options}
            />
        </ConfigProvider>
    );
};
